import { atLeastXl } from "@scripts/breakpoints";
import { prefersReducedMotion } from "@scripts/motion";
import type { ComponentInit } from "@scripts/registry";
import { FA_TAB_SELECT, SELECTED_TAB } from "@scripts/tabs";
import { createTracker } from "@scripts/tracker";

const TRACK = "[data-fa-tab-bar-track]";
const NAV = "[data-fa-tab-bar-nav]";
const TRACKER = "[data-fa-tab-bar-tracker]";
const TAB = "[data-fa-tab]";
const EMBLEM = "[data-fa-tab-bar-emblem]";

const TRACKING = "fa-tab-bar__nav--tracking";
const BOUNCING = "fa-tab-bar__emblem--bouncing";
const OVERFLOW_START = "fa-tab-bar--overflow-start";
const OVERFLOW_END = "fa-tab-bar--overflow-end";

const KEYFRAMES = "fa-emblem-bounce";
const SCROLL_SLACK = 1;

export const initTabBar: ComponentInit = (root) => {
	const track = root.querySelector<HTMLElement>(TRACK);
	const nav = root.querySelector<HTMLElement>(NAV);

	if (!track || !nav) return;

	const listeners = new AbortController();
	const options = { signal: listeners.signal };

	const syncEdgeFades = () => {
		const scrollable = track.scrollWidth - track.clientWidth;

		root.classList.toggle(OVERFLOW_START, track.scrollLeft > SCROLL_SLACK);
		root.classList.toggle(OVERFLOW_END, scrollable - track.scrollLeft > SCROLL_SLACK);
	};

	const overhang = (item: HTMLElement) => {
		const box = item.getBoundingClientRect();
		const trackBox = track.getBoundingClientRect();

		return { behind: box.left - trackBox.left, past: box.right - trackBox.right };
	};

	const scrollBy = (delta: number) => {
		if (delta === 0) return;

		track.scrollTo({
			left: Math.max(
				0,
				Math.min(track.scrollLeft + delta, track.scrollWidth - track.clientWidth),
			),
			behavior: "auto",
		});
	};

	let aligned: { item: HTMLElement; edge: "behind" | "past" } | null = null;

	const scrollIntoView = (item: HTMLElement | null) => {
		aligned = null;

		if (!item) return;

		const { behind, past } = overhang(item);

		/* A tab already in view has a negative `past`, and scrolling by it drags the row left. */
		if (behind < 0) aligned = { item, edge: "behind" };
		else if (past > 0) aligned = { item, edge: "past" };
		else return;

		scrollBy(overhang(item)[aligned.edge]);
	};

	const currentItem = () => nav.querySelector<HTMLElement>(SELECTED_TAB);

	const bounce = () => {
		if (prefersReducedMotion()) return;
		if (!atLeastXl()) return;

		currentItem()?.querySelector<HTMLElement>(EMBLEM)?.classList.add(BOUNCING);
	};

	/* Only a return counts: `onSettle` also fires on a resize with the same item, and on hovering
	   the current item, and neither is a return. */
	let away = false;

	const onSettle = (item: HTMLElement | null) => {
		const current = currentItem();

		if (!current) {
			away = false;
			return;
		}

		if (item !== current) {
			away = true;
			return;
		}

		if (!away) return;

		away = false;
		bounce();
	};

	scrollIntoView(currentItem());
	syncEdgeFades();

	nav.addEventListener(FA_TAB_SELECT, (event) => scrollIntoView(event.detail.tab), options);

	/* The select handler measures before the emblems resize, so the row settles off the tab's edge. */
	nav.addEventListener(
		"transitionend",
		(event) => {
			if (!aligned || event.propertyName !== "width") return;
			if (!(event.target as Element).matches(EMBLEM)) return;

			scrollBy(overhang(aligned.item)[aligned.edge]);
		},
		options,
	);

	track.addEventListener("scroll", syncEdgeFades, { ...options, passive: true });

	/* A track that starts wide enough not to scroll fires no scroll event when it later becomes
	   narrow, so the fades would stay off until the reader scrolled. */
	const resize = new ResizeObserver(syncEdgeFades);

	resize.observe(track);

	root.addEventListener(
		"animationend",
		(event) => {
			if (event.animationName === KEYFRAMES)
				(event.target as HTMLElement).classList.remove(BOUNCING);
		},
		options,
	);

	const tracker = root.querySelector<HTMLElement>(TRACKER);
	const teardownTracker = tracker
		? createTracker({
				root: nav,
				tracker,
				item: TAB,
				restTarget: currentItem,
				trackingClass: TRACKING,
				restAttributes: ["aria-selected"],
				followsHeight: false,
				onSettle,
			})
		: undefined;

	bounce();

	return () => {
		listeners.abort();
		resize.disconnect();
		teardownTracker?.();
		root.classList.remove(OVERFLOW_START, OVERFLOW_END);

		for (const emblem of root.querySelectorAll(EMBLEM)) emblem.classList.remove(BOUNCING);

		if (tracker && !tracker.getAttribute("style")) tracker.removeAttribute("style");
	};
};
